import { FocusEvent } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import cn from 'classnames';
import { connect } from 'react-redux';

import { getCountryCode } from '@lendingAPI/lendingAPISelector';
import { RootState } from '@src/reducers';

import { convertMonthlyToYearly } from './BusinessIntroduction.helpers';
import messages from './BusinessIntroduction.messages';
import limitedCompanyStyles from './LimitedCompanyFields.module.css';
import FormField from '../../components/FormField';
import { numberWithCommas, numberWithoutCommas } from '../../helpers/helpers';
import normalizer from '../../helpers/inputNormalizer';
import { getSplitTestValue } from '../../utils/selectors';

const mapStateToProps = (state: RootState) => ({
    isInSplitTestMonthlyRevenue: getSplitTestValue(
        state,
        'monthly_revenue_question'
    ),
    isPartner:
        state.lendingAPI.profile?.data?.user_type === 'partner_developer',
    isBusinessDetailsReductionSplitTest:
        getSplitTestValue(state, 'business_details_reduction') &&
        getCountryCode(state) === 'uk'
});

type FieldProps = {
    name: string;
    value: number;
    initialValue: number;
    onChange: (value: any) => void;
    onBlur: (event: FocusEvent<HTMLInputElement>) => void;
    onFocus: () => void;
    touched: boolean;
    dirty: boolean;
    active: boolean;
    visited: boolean;
};

type TurnoverQuestionProps = {
    annualTurnoverField: FieldProps;
    monthlyTurnoverField: FieldProps;
    disabled: boolean;
    turnoverSinceTrading: boolean | string;
    isInSplitTestMonthlyRevenue: boolean;
    isPartner: boolean;
    isBusinessDetailsReductionSplitTest?: boolean;
};

const TurnoverQuestion = ({
    annualTurnoverField,
    monthlyTurnoverField,
    disabled,
    turnoverSinceTrading,
    isInSplitTestMonthlyRevenue,
    isBusinessDetailsReductionSplitTest,
    isPartner
}: TurnoverQuestionProps) => {
    const label =
        isInSplitTestMonthlyRevenue && !isPartner
            ? `What is your typical monthly turnover?`
            : turnoverSinceTrading
            ? 'Turnover since you started trading'
            : `Last 12 months' turnover`;

    let turnoverName: string, placeHolderText, turnoverField;

    if (isInSplitTestMonthlyRevenue && !isPartner) {
        turnoverField = monthlyTurnoverField;
        placeHolderText = Number(25000).toLocaleString('en-GB');
        turnoverName = 'monthly_turnover';
    } else {
        turnoverField = annualTurnoverField;
        placeHolderText = Number(250000).toLocaleString('en-GB');
        turnoverName = 'annual_turnover';
    }

    const normalizedTurnover = normalizer(
        turnoverField,
        numberWithCommas,
        numberWithoutCommas
    );

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = numberWithoutCommas(event.target.value);

        // overriding default onChange callback function
        if (isInSplitTestMonthlyRevenue && !isPartner) {
            const annualValue = convertMonthlyToYearly(value as number);
            annualTurnoverField.onChange(annualValue);
            monthlyTurnoverField.onChange(value);
        }
    };

    return (
        <FormField
            wrapperGrid={
                isBusinessDetailsReductionSplitTest
                    ? { xs: 12, sm: 12 }
                    : { xs: 12, sm: 6 }
            }
            wrapperClassName={
                isBusinessDetailsReductionSplitTest
                    ? limitedCompanyStyles.turnoverContainerBusinessDetailsReduction
                    : ''
            }
            field={normalizedTurnover}
            label={label}
            labelProps={{
                htmlFor: `company.${turnoverName}`,
                className: isBusinessDetailsReductionSplitTest
                    ? limitedCompanyStyles.turnoverQuestionLabel
                    : ''
            }}
            tooltip={
                isInSplitTestMonthlyRevenue && !isPartner
                    ? messages.monthlyTurnoverTooltip.defaultMessage
                    : messages.turnoverTooltip.defaultMessage
            }>
            <FormField.Input
                id={`company.${turnoverName}`}
                name={turnoverName}
                handleChange={handleChange}
                whenBlur={(event: FocusEvent<HTMLInputElement>) => {
                    if (event.target.value.length > 0) {
                        tracking.ampli.formFieldCompleted({
                            form_field: turnoverName
                        });
                    }
                }}
                whenFocus={() => {
                    tracking.ampli.formFieldStarted({
                        form_field: turnoverName
                    });
                }}
                addon={true}
                addonContent="£"
                addonClassName={cn({ disabled })}
                pattern="\d*"
                placeholder={placeHolderText}
                disabled={disabled}
            />
        </FormField>
    );
};

export default connect(mapStateToProps)(TurnoverQuestion);
